import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cls } from "app/utils";
import styles from "./styles.module.scss";

interface ExpandableDescriptionProps {
  text?: string | null;
  // The number of lines that stay visible in the collapsed state.
  lines?: number;
}

// This component clamps a description to `lines`. It shows a Show More toggle
// only when the text overflows the clamp. A booking option description holds up
// to 1000 characters. A row that shows the full text overruns its own layout.
// The public landing page uses the same pattern in `PrivateSessions.tsx`.
const ExpandableDescription: React.FC<ExpandableDescriptionProps> = ({
  text,
  lines = 2,
}) => {
  const { t } = useTranslation();
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const hasText = Boolean(text?.trim());

  useEffect(() => {
    // A list holds many rows, and most rows carry no description. Such a row
    // renders nothing, so it needs no resize listener.
    if (!hasText) {
      return undefined;
    }

    const measure = () => {
      const element = textRef.current;
      // The clamp creates the overflow. In the expanded state there is no
      // overflow to measure. Keep the result of the last collapsed measurement.
      if (!element || isExpanded) {
        return;
      }

      setIsTruncated(element.scrollHeight > element.clientHeight + 1);
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [hasText, text, lines, isExpanded]);

  if (!hasText) {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      <p
        ref={textRef}
        className={cls(styles.text, { [styles.clamped]: !isExpanded })}
        style={{ WebkitLineClamp: lines }}
      >
        {text}
      </p>
      {isTruncated && (
        <button
          type="button"
          className={styles.toggle}
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded((expanded) => !expanded)}
        >
          {isExpanded ? t("Show Less") : t("Show More")}
        </button>
      )}
    </div>
  );
};

export default ExpandableDescription;
